import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { FilterPills, nonEmptyPills, withCount } from '@/components/FilterPills';
import { PageHeader, PageShell } from '@/components/PageShell';
import { BrandStatusBadge } from '@/components/BrandChip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useDebouncedSearchParam, useSetSearchParam } from '@/lib/searchParams';
import { useServerSort } from '@/lib/tableSort';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isCatalogableBrandName } from '@rock/brands';
import { useLoadMore } from '@rock/db-admin';
import { brandStatusCountsState, brandsState, sharedNameplateTableState } from '@/state';
import { useAsyncAtomValue, useFreshOnMountAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import type { Brand } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import { BadgeCheck } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    BRAND_REVIEW_STATUS_FILTER_LABELS,
    BRAND_REVIEW_STATUS_FILTERS,
    type BrandReviewStatusFilter,
    type BrandReviewStatusOption,
} from './brandReviewStatus';
import { isDomainAlignedWithBrandfetch } from './brandfetchAlignment';
import { createBrandFromName } from './brandCatalogActions';
import { rebuildBrandSearchIndexNow } from './brandSearchIndexApi';
import { useCurrentUserId } from '@/db/useCurrentUserId';

const log = logger('BrandsListPage');

export const BrandsListPage: FC = () => {
    // Filters live in the URL (not just component state) so they survive the row-click → details → back
    // round trip, matching the models list's convention.
    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status');
    const statusFilter = BRAND_REVIEW_STATUS_FILTERS.find((status) => status === statusParam) ?? 'all';
    // Junk (and merged) brands are hidden from "All statuses" unless asked for; their own filter shows them.
    const includeJunk = searchParams.get('junk') === '1';
    const allFilter: BrandReviewStatusFilter = includeJunk ? 'all' : 'live';
    const queryFilter: BrandReviewStatusFilter = statusFilter === 'all' ? allFilter : statusFilter;
    const searchQuery = searchParams.get('q') ?? '';
    const setSearchParam = useSetSearchParam();
    const { input: searchInput, onChange: handleSearchChange } = useDebouncedSearchParam('q');
    // Counts cover the whole catalog, so a search makes them wrong for what's on screen.
    const { value: catalogCounts } = useFreshOnMountAtomValue(brandStatusCountsState);
    const statusCounts = searchQuery ? undefined : catalogCounts;
    const statusPills = nonEmptyPills(
        BRAND_REVIEW_STATUS_FILTERS.filter((status) => status !== 'all'),
        BRAND_REVIEW_STATUS_FILTER_LABELS,
        statusCounts,
        statusFilter === 'all' ? undefined : statusFilter,
    );

    const {
        data: brands,
        isLoading,
        hasError,
        hasMore,
        loadMore,
        filter,
        setFilter,
        sort,
        setSort,
    } = useLoadMore(brandsState);
    // The atom's filter drives the (server-side) query; the URL is the source of truth for both dimensions.
    useEffect(() => {
        if (filter.reviewStatus !== queryFilter || filter.namePrefix !== searchQuery) {
            setFilter({ reviewStatus: queryFilter, namePrefix: searchQuery });
        }
    }, [queryFilter, searchQuery, filter, setFilter]);

    // A prefix search pins Firestore's ordering to the name it searches.
    const sortDisabledReason = (column: DataTableColumn<Brand>) =>
        searchQuery && column.sortField !== 'canonicalName'
            ? 'Sorting by this column is unavailable while searching — the search pins the order to Name.'
            : undefined;
    const { sort: tableSort, setSort: setTableSort } = useServerSort(
        BRAND_COLUMNS,
        { sort, setSort },
        sortDisabledReason,
    );

    const navigate = useNavigate();
    const by = useCurrentUserId();
    const sharedNameplates = useAsyncAtomValue(sharedNameplateTableState, []);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [brandNameInput, setBrandNameInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRebuilding, setIsRebuilding] = useState(false);

    // Republish the app's brand-search index on demand — the daily schedule otherwise leaves up to a
    // day of lag between curating a brand here and it appearing in the app's typeahead.
    const handleRebuildIndex = async () => {
        setIsRebuilding(true);
        try {
            const { entryCount } = await rebuildBrandSearchIndexNow();
            toast.success('Brand search index rebuilt', {
                description: `${entryCount} brand(s) published to the app.`,
            });
        } catch (err) {
            log.error('Failed to rebuild brand search index', { err });
            toast.error('Unable to rebuild search index', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsRebuilding(false);
        }
    };

    const brandName = brandNameInput.trim();
    const catalogable = useMemo(
        () => isCatalogableBrandName(brandName, sharedNameplates),
        [brandName, sharedNameplates],
    );
    const preview = useMemo(() => (catalogable ? normalizeBrandName(brandName) : undefined), [brandName, catalogable]);
    const canSubmit = catalogable && !isSubmitting && !!by;

    const handleOpenDialog = () => {
        setBrandNameInput('');
        setDialogOpen(true);
    };

    const handleAddBrand = async () => {
        if (!canSubmit || !by) return;

        setIsSubmitting(true);
        try {
            const outcome = await createBrandFromName(brandName, by);
            if (outcome.status === 'refused') {
                toast.error('That name can’t be cataloged', { description: outcome.reason });
                return;
            }
            if (outcome.status === 'exists') {
                toast.info(`“${brandName}” is already cataloged`, { description: 'Opening the existing brand.' });
            } else {
                toast.success(`Brand “${outcome.brand.canonicalName}” created`);
            }
            setDialogOpen(false);
            navigate(`/products/brands/${encodeURIComponent(outcome.brand.id)}`);
        } catch (err) {
            log.error('Failed to create brand', { err });
            toast.error('Unable to create brand', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const headerActions = (
        <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRebuildIndex} disabled={isRebuilding}>
                {isRebuilding ? 'Rebuilding…' : 'Rebuild search index'}
            </Button>
            <Button onClick={handleOpenDialog}>Add Brand</Button>
        </div>
    );
    const setStatusFilter = (status: BrandReviewStatusOption) =>
        setSearchParam('status', status === 'all' ? undefined : status);

    return (
        <PageShell>
            <PageHeader
                title="Brands"
                description="Explore the brands that power Rock's equipment matching and metadata services."
            >
                <Input
                    placeholder="Search brands…"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-[220px]"
                />
                <Select
                    value={statusFilter}
                    onValueChange={(value) =>
                        setStatusFilter(BRAND_REVIEW_STATUS_FILTERS.find((status) => status === value) ?? 'all')
                    }
                >
                    <SelectTrigger className="w-[200px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {BRAND_REVIEW_STATUS_FILTERS.map((status) => (
                            <SelectItem key={status} value={status}>
                                {withCount(
                                    BRAND_REVIEW_STATUS_FILTER_LABELS[status],
                                    statusCounts?.[status === 'all' ? allFilter : status],
                                )}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {statusFilter === 'all' && (
                    <label htmlFor="brands-include-junk" className="flex items-center gap-2 text-sm">
                        <Checkbox
                            id="brands-include-junk"
                            checked={includeJunk}
                            onCheckedChange={(checked) => setSearchParam('junk', checked === true ? '1' : undefined)}
                        />
                        Include Junk / Merged
                    </label>
                )}
            </PageHeader>
            {!searchQuery && (
                <div className="rounded border bg-background p-3">
                    <FilterPills
                        label="Status"
                        items={statusPills}
                        selected={statusFilter === 'all' ? undefined : statusFilter}
                        allCount={statusCounts?.[allFilter]}
                        onSelect={(key) =>
                            setStatusFilter(BRAND_REVIEW_STATUS_FILTERS.find((status) => status === key) ?? 'all')
                        }
                    />
                </div>
            )}
            {brands.length === 0 ? (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>
                                    {hasError
                                        ? 'Couldn’t load brands'
                                        : isLoading
                                          ? 'Loading brands…'
                                          : statusFilter === 'all' && !searchQuery
                                            ? 'No brands found'
                                            : 'No brands match your filters'}
                                </CardTitle>
                                <CardDescription>
                                    {hasError
                                        ? 'You may not have permission to view this page.'
                                        : isLoading
                                          ? 'Fetching the brand catalog.'
                                          : statusFilter === 'all' && !searchQuery
                                            ? 'Once brands are captured in Firestore they will appear in this list.'
                                            : 'Try a different search or status filter.'}
                                </CardDescription>
                            </div>
                            {headerActions}
                        </div>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Brands</CardTitle>
                                <CardDescription>Tap any row to inspect the brand record.</CardDescription>
                            </div>
                            {headerActions}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <DataTable
                            columns={BRAND_COLUMNS}
                            rows={brands}
                            rowKey={(brand) => brand.id}
                            rowHref={(brand) => `/products/brands/${encodeURIComponent(brand.id)}`}
                            sort={tableSort}
                            onSortChange={setTableSort}
                            sortDisabledReason={sortDisabledReason}
                        />
                        {hasMore && (
                            <div className="mt-4 flex justify-center">
                                <Button variant="outline" onClick={() => loadMore()}>
                                    Load More
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
            <Dialog open={dialogOpen} onOpenChange={(isOpen) => !isSubmitting && setDialogOpen(isOpen)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Brand</DialogTitle>
                        <DialogDescription>
                            Creates the brand in the catalog and opens its page, where “Fetch from Brandfetch” adds its
                            logo and company info.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-2 py-2">
                        <Input
                            placeholder="Brand name (e.g. Whirlpool)"
                            value={brandNameInput}
                            onChange={(e) => setBrandNameInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canSubmit) {
                                    void handleAddBrand();
                                }
                            }}
                            disabled={isSubmitting}
                            autoFocus
                        />
                        {preview ? (
                            <div className="section-description">
                                Will be cataloged as <span className="font-medium">“{preview.display}”</span> — key{' '}
                                <span className="font-mono">{preview.key}</span>
                            </div>
                        ) : brandName ? (
                            <div className="text-sm text-destructive">
                                Not a catalogable brand name — placeholders like “Unknown” aren’t allowed.
                            </div>
                        ) : null}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={handleAddBrand} disabled={!canSubmit}>
                            {isSubmitting ? 'Adding…' : 'Add Brand'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageShell>
    );
};

/**
 * Logo and Website don't sort: one is an image, the other an optional field whose ordering (by URL
 * string) tells you nothing worth a click. Status doesn't either — Firestore won't order by a field
 * an equality filter already pins, and the status filter above answers that question properly.
 */
const BRAND_COLUMNS: DataTableColumn<Brand>[] = [
    {
        key: 'key',
        header: 'Key',
        className: 'min-w-[180px]',
        sortField: 'canonicalKey',
        cell: (brand) => brand.canonicalKey,
    },
    {
        key: 'name',
        header: 'Name',
        className: 'min-w-[200px]',
        cellClassName: 'font-medium',
        sortField: 'canonicalName',
        cell: (brand) => brand.displayName ?? brand.canonicalName,
    },
    {
        key: 'logo',
        header: 'Logo',
        className: 'min-w-[100px]',
        cell: (brand) =>
            brand.logo ? (
                <img
                    src={brand.logo.thumbnail}
                    alt={`${brand.canonicalName} logo`}
                    className="h-8 w-auto max-w-[120px] object-contain"
                />
            ) : null,
    },
    {
        key: 'website',
        header: 'Website',
        className: 'min-w-[180px]',
        cell: (brand) =>
            brand.website ? (
                <span className="flex items-center gap-1.5">
                    <a
                        href={brand.website}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary underline-offset-2 hover:underline"
                    >
                        {brand.website}
                    </a>
                    {isDomainAlignedWithBrandfetch(brand) ? (
                        <span title="Matches Brandfetch">
                            <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Matches Brandfetch" />
                        </span>
                    ) : null}
                </span>
            ) : null,
    },
    {
        key: 'status',
        header: 'Status',
        className: 'min-w-[140px]',
        cell: (brand) => <BrandStatusBadge brand={brand} />,
    },
];
